import { useState, useEffect, useCallback, useRef } from 'react';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { Scan, PageResult } from '../types';
import {
  GET_SCAN,
  LIST_PAGE_RESULTS_SUMMARY,
  LIST_PAGE_RESULTS_ACCESSIBILITY,
  LIST_PAGE_RESULTS_EAA,
  LIST_PAGE_RESULTS_SEO,
  LIST_PAGE_RESULTS_GEO,
  LIST_PAGE_RESULTS_BROKEN_LINKS,
  LIST_PAGE_RESULTS_TOXIC_LINKS,
} from '../graphql/queries';

const client = generateClient();

interface GetScanResponse {
  getScan: Scan;
}

interface ListPageResultsResponse {
  listPageResults: { items: PageResult[]; nextToken?: string | null };
}

const IN_PROGRESS_STATUSES = ['PENDING', 'CRAWLING', 'SCANNING', 'AGGREGATING', 'GENERATING_REPORT'];

const TAB_QUERIES: Record<string, string> = {
  accessibility: LIST_PAGE_RESULTS_ACCESSIBILITY,
  eaa: LIST_PAGE_RESULTS_EAA,
  seo: LIST_PAGE_RESULTS_SEO,
  geo: LIST_PAGE_RESULTS_GEO,
  links: LIST_PAGE_RESULTS_BROKEN_LINKS,
  'toxic-links': LIST_PAGE_RESULTS_TOXIC_LINKS,
};

// Amplify v6 throws the full GraphQLResult when errors are present alongside data.
// Catch and return it so callers can still use the partial data.
async function safeGraphQL<T>(promise: Promise<GraphQLResult<T>>): Promise<GraphQLResult<T>> {
  try {
    return await promise;
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'data' in err) {
      console.warn('GraphQL partial error (using data anyway):', (err as { errors?: unknown }).errors);
      return err as GraphQLResult<T>;
    }
    throw err;
  }
}

export function useScanResults(siteId: string, scanId: string) {
  const [scan, setScan] = useState<Scan | null>(null);
  const [pageResults, setPageResults] = useState<PageResult[]>([]);
  const [tabData, setTabData] = useState<Record<string, PageResult[]>>({});
  const [loadingTabs, setLoadingTabs] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  // Track which tabs have been requested (loaded or in-flight) to avoid duplicate fetches
  const loadedTabsRef = useRef<Set<string>>(new Set());

  const fetchResults = useCallback(async (isPolling = false) => {
    if (!isPolling) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Fetch scan metadata + summary page results (no heavy fields) in parallel.
      // fields: [] strips all heavy fields so a 444-page summary is ~50KB.
      const [scanResponse, resultsResponse] = await Promise.all([
        safeGraphQL(client.graphql({
          query: GET_SCAN,
          variables: { siteId, scanId },
        }) as Promise<GraphQLResult<GetScanResponse>>),
        safeGraphQL(client.graphql({
          query: LIST_PAGE_RESULTS_SUMMARY,
          variables: { scanId, sortByScore: true },
        }) as Promise<GraphQLResult<ListPageResultsResponse>>),
      ]);

      setScan(scanResponse.data?.getScan || null);
      setPageResults(resultsResponse.data?.listPageResults?.items || []);
    } catch (err) {
      console.error('Error fetching scan results:', err);
      if (!isPolling) {
        setError('Failed to load scan results.');
      }

      // Mock data for development
      if (import.meta.env.DEV && !isPolling) {
        setScan(getMockScan(siteId, scanId));
        setPageResults(getMockPageResults(scanId));
      }
    } finally {
      if (!isPolling) {
        setIsLoading(false);
      }
    }
  }, [siteId, scanId]);

  const loadTabData = useCallback(async (tab: string) => {
    const query = TAB_QUERIES[tab];
    if (!query) return; // No per-tab query for this tab (e.g. summary, performance, issues)

    if (loadedTabsRef.current.has(tab)) return; // Already loaded or in-flight
    loadedTabsRef.current.add(tab);

    setLoadingTabs(prev => new Set(prev).add(tab));

    const allPages: PageResult[] = [];
    let nextToken: string | undefined;

    try {
      do {
        const response = await safeGraphQL(client.graphql({
          query,
          variables: { scanId, limit: 50, nextToken },
        }) as Promise<GraphQLResult<ListPageResultsResponse>>);

        const result = response.data?.listPageResults;
        if (result?.items) {
          allPages.push(...result.items);
        }
        nextToken = result?.nextToken ?? undefined;
      } while (nextToken);

      setTabData(prev => ({ ...prev, [tab]: allPages }));
    } catch (err) {
      console.error(`Error loading tab data for ${tab}:`, err);
      // Remove from loaded set so a retry is possible
      loadedTabsRef.current.delete(tab);
    } finally {
      setLoadingTabs(prev => {
        const next = new Set(prev);
        next.delete(tab);
        return next;
      });
    }
  }, [scanId]);

  // Initial fetch
  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Polling for in-progress scans
  useEffect(() => {
    const isInProgress = scan && IN_PROGRESS_STATUSES.includes(scan.status.toUpperCase());

    if (isInProgress) {
      // Poll every 3 seconds for in-progress scans
      pollingRef.current = setInterval(() => {
        fetchResults(true);
      }, 3000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [scan?.status, fetchResults]);

  return {
    scan,
    pageResults,
    tabData,
    loadTabData,
    loadingTabs,
    isLoading,
    error,
    refetch: fetchResults,
  };
}

function getMockScan(siteId: string, scanId: string): Scan {
  return {
    siteId,
    scanId,
    userId: 'user-1',
    status: 'completed',
    progress: {
      phase: 'completed',
      pagesDiscovered: 45,
      pagesScanned: 45,
    },
    config: {
      siteUrl: 'https://example.com',
      scanTypes: ['accessibility', 'eaa', 'seo', 'performance', 'brokenLinks'],
      wcagVersion: '2.2',
      wcagLevel: 'AA',
      pageLimit: 100,
      rateLimit: 'gentle',
    },
    results: {
      overallScore: 78,
      overallGrade: 'C',
      totalIssues: {
        critical: 3,
        serious: 12,
        moderate: 28,
        minor: 45,
        total: 88,
      },
    },
    startedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    completedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 15 * 60 * 1000).toISOString(),
  };
}

function getMockPageResults(scanId: string): PageResult[] {
  return [
    {
      scanId,
      pageUrl: 'https://example.com/',
      score: 85,
      grade: 'B',
      issueCount: 12,
      issues: { critical: 0, serious: 2, moderate: 4, minor: 6, total: 12 },
      scannedAt: new Date().toISOString(),
    },
    {
      scanId,
      pageUrl: 'https://example.com/about',
      score: 72,
      grade: 'C',
      issueCount: 18,
      issues: { critical: 1, serious: 3, moderate: 6, minor: 8, total: 18 },
      scannedAt: new Date().toISOString(),
    },
    {
      scanId,
      pageUrl: 'https://example.com/contact',
      score: 91,
      grade: 'A',
      issueCount: 5,
      issues: { critical: 0, serious: 0, moderate: 2, minor: 3, total: 5 },
      scannedAt: new Date().toISOString(),
    },
    {
      scanId,
      pageUrl: 'https://example.com/products',
      score: 65,
      grade: 'D',
      issueCount: 25,
      issues: { critical: 2, serious: 5, moderate: 8, minor: 10, total: 25 },
      scannedAt: new Date().toISOString(),
    },
    {
      scanId,
      pageUrl: 'https://example.com/blog',
      score: 78,
      grade: 'C',
      issueCount: 15,
      issues: { critical: 0, serious: 2, moderate: 8, minor: 5, total: 15 },
      scannedAt: new Date().toISOString(),
    },
  ];
}
